import {
  Button,
  DatePicker,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  message,
} from "antd";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { changeStatus, getCodeDetails } from "Network"; // Add API function for fetching specific code details
import moment from "moment";
import Header from "modules/SubComponents/Header";
import SubHeader from "modules/SubComponents/SubHeader";
import { dropdownStyle } from "helpers/helpfulFunctions";
import { toastError, toastSuccess } from "helpers/toasters";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";

const { RangePicker } = DatePicker;

function CodeDetails() {
  const { prefex } = useParams(); // Get the prefex from URL

  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [open, setOpen] = useState(null);
  const [status, setStatus] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [filteredData, setFilteredData] = useState(codes);
  const [dateRange, setDateRange] = useState([null, null]);
  const [filters, setFilters] = useState({});
  const [isUsed, setIsUsed] = useState("All");

  const prefexName = codes[0]?.code.slice(0, 3);

  const {
    authorization: { email: adminEmail, isCeo, isCodesManager },
  } = useSelector((state) => state?.auth);

  // Fetch code details based on the prefex
  useEffect(() => {
    const fetchCodeDetails = () => {
      setLoading(true);
      getCodeDetails(
        (success) => {
          if (success?.isSuccesss) {
            setCodes(success.data);
            setLoading(false);
          } else {
            message.error(success.err_msg);
            setLoading(false);
          }
        },
        (fail) => {
          message.error(fail?.data?.err_msg || "Failed to fetch code details");
          setLoading(false);
        },
        prefex,
        filters
      );
    };
    fetchCodeDetails();
  }, [filters]);

  useEffect(() => {
    setFilteredData(codes);
  }, [codes]);

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8, display: "flex", flexDirection: "column" }}>
        <Input
          placeholder={`Search...`}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <div className="d-flex justify-content-between">
          <Button
            style={{ width: 90 }}
            onClick={() => {
              clearFilters();
              confirm();
              setFilteredData([]);
            }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            style={{ width: 90, display: "flex", alignItems: "center" }}
          >
            Search
          </Button>
        </div>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) => {
      if (dataIndex === "duration") {
        return record[dataIndex]
          ? record[dataIndex].slice(0, 2).trim().includes(value.toLowerCase())
          : "";
      } else {
        return record[dataIndex]
          ? record[dataIndex]
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase())
          : "";
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? <span>{text}</span> : text,
  });

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      const [start, end] = dates;
      setFilters((preState) => {
        return {
          ...preState,
          from: start?.toISOString(),
          to: end?.toISOString(),
        };
      });
    } else {
      // Reset to all codes when no range is selected
      setFilteredData(codes);
      setFilters((prevState) => {
        delete prevState.from;
        delete prevState.to;
        return { ...prevState };
      });
    }
  };

  const columns = [
    {
      title: "CODE",
      key: "code",
      dataIndex: "code",
      ...getColumnSearchProps("code"),
      // sorter: (a, b) => (a?.code > b?.code ? 1 : -1),
      sorter: (a, b) => (a?.code || "").localeCompare(b?.code || ""),
    },

    {
      title: "PRICE",
      key: "price",
      dataIndex: "price",
      render: (price) => <span>{price ? `${price} LE` : "-"}</span>,
    },

    {
      title: "USED/UNUSED",
      key: "used",
      render: (_, record) => <span>{record.userName ? "Used" : "Unused"}</span>,
      sorter: (a, b) => (a?.userName || "").localeCompare(b?.userName || ""),
      filterDropdown: ({ confirm }) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: "8px",
              width: "150px",
              alignItems: "center",
            }}
          >
            <Radio.Group
              defaultValue={"All"}
              value={isUsed}
              className="flex flex-column justify-content-center py-2 align-self-start"
              onChange={(e) => {
                setIsUsed(e.target.value);
              }}
            >
              <Radio value={"All"}>All</Radio>
              <Radio value={true}>Used</Radio>
              <Radio value={false}>Unused</Radio>
            </Radio.Group>
            <Space>
              <Button
                size="small"
                onClick={() => {
                  confirm();
                  setIsUsed("All");
                  if (isUsed === "All") return;
                  else
                    setFilters((preValue) => {
                      delete preValue.isUsed;
                      return { ...preValue };
                    });
                }}
              >
                Reset
              </Button>
              <Button
                onClick={() => {
                  confirm();
                  setFilters((preValue) => {
                    if (isUsed === "All") {
                      delete preValue.isUsed;
                      return { ...preValue };
                    } else {
                      return { ...preValue, isUsed };
                    }
                  });
                }}
                size="small"
                type="primary"
              >
                Search
              </Button>
            </Space>
          </div>
        );
      },

      filterIcon: () => (
        <FilterOutlined
          style={{
            color: filters.hasOwnProperty("isUsed") > 0 ? "#1890ff" : undefined,
          }}
        />
      ),
    },

    {
      title: "USED DATE",
      key: "usageDate",
      render: (_, record) =>
        record.usageDate ? moment(record.usageDate).format("DD-MM-YYYY") : "-",
      filterDropdown: () => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              padding: ".5rem",
            }}
          >
            <RangePicker
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
              style={{ margin: "20px 0", width: "100%", margin: "auto" }}
            />
          </div>
        );
      },
      sorter: (a, b) => {
        const dateA = a?.usageDate ? new Date(a.usageDate).getTime() : 0;
        const dateB = b?.usageDate ? new Date(b.usageDate).getTime() : 0;
        return dateA - dateB; // Ascending order
      },
      filterIcon: () => (
        <FilterOutlined
          style={{
            color: filters.hasOwnProperty("from") > 0 ? "#1890ff" : undefined,
          }}
        />
      ),
    },
    {
      title: "User Name",
      key: "userName",
      dataIndex: "userName",
      render: (_, record) => (
        <span>{record.userName ? record.userName : "-"}</span>
      ),
      ...getColumnSearchProps("userName"),
      sorter: (a, b) => (a?.userName > b?.userName ? 1 : -1),
    },
    {
      title: "USER NUMBER",
      key: "userNumber",
      dataIndex: "userNumber",
      render: (_, record) => (
        <span>{record.userNumber ? record.userNumber : "-"}</span>
      ),
      ...getColumnSearchProps("userNumber"),
      // sorter: (a, b) => (a?.userNumber > b?.userNumber ? 1 : -1),
      sorter: (a, b) =>
        (a?.userNumber || "").localeCompare(b?.userNumber || ""),
    },
    // STATUS
    {
      title: "STATUS",
      key: "isActive",
      dataIndex: "isActive",
      render: (_, record) => (
        <span>{record.isActive ? "Active" : "Inactive"}</span>
      ),
      sorter: (a, b) => (a?.isActive > b?.isActive ? 1 : -1),
      filterIcon: () => <FilterOutlined />,
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
      // onFilter: (value, record) => record?.isActive === value,
      onFilter: (value, record) => {
        // Handle multiple filter values
        const selectedFilters = Array.isArray(value) ? value : [value];
        console.log("🚀 ~ CodeDetails ~ selectedFilters:", selectedFilters);
        return selectedFilters.includes(record?.isActive);
      },
    },
    // {
    //   title: "STATUS",
    //   key: "status",
    //   dataIndex: "status",
    //   render: (_, record) => {
    //     return (
    //       <Space size={10}>
    //         <Switch
    //           disabled={!(isCeo || isCodesManager)}
    //           style={{
    //             backgroundColor: `${
    //               record?.isActive ? "rgb(49, 170, 74)" : "gray"
    //             }`,
    //             minWidth: "35px",
    //           }}
    //           checked={record?.isActive}
    //           // onChange={() => () => showDeleteModal(record)}
    //           // onClick={() => showDeleteModal(record)}
    //         />
    //         <span>{record?.isActive ? "Active" : "Inactive"}</span>
    //       </Space>
    //     );
    //   },
    //   filters: [
    //     { text: "Active", value: true },
    //     { text: "Inactive", value: false },
    //   ],
    //   onFilter: (value, record) => record?.isActive === value,
    // },
  ];

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleChangeStatus = () => {
    if (status) {
      const data = {
        isActive: status === "Active",
        codeIds: selectedRowKeys,
      };

      changeStatus(
        data,
        (success) => {
          if (success.isSuccess) {
            toastSuccess(success.data.message);
            setOpen(false);
            setStatus(null);
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            toastError(success.err_msg);
          }
        },
        (fail) => {
          toastError(fail?.data?.err_msg || "Failed to change status");
        }
      );
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setStatus(null);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: !!record.userName,
    }),
  };

  // Determine table data source
  const getTableDataSource = () => {
    return filteredData.length ? filteredData : codes;
  };

  // const exportData = getTableDataSource().map((code) => {
  const exportData = filteredData?.map((code) => {
    return {
      Code: code.code,
      Price: code.price,
      "Used/Unused": code.userName ? "Used" : "Unused",
      "Used Date": moment(code.usageDate).format("DD-MM-YYYY"),
      Username: code.userName,
      "User Number": code.userNumber,
      Status: code.isActive ? "Active" : "Inactive",
    };
  });

  const hasPermission = isCeo || isCodesManager;

  return (
    <>
      <section className="code-details">
        <Header
          title="Code Details"
          exportAsExcel
          columnsForExport={columns}
          reportName={`CODE PREFIX (${prefexName})`}
          // dataSource={filteredData.length > 0 ? filteredData : codes}
          // dataSource={filteredData}
          dataSource={exportData}
        />
        <SubHeader bgColor="red" title={`Code prefix (${prefexName})`} />
        <div className="cast-data__table  grid-table ">
          {/* {selectedRowKeys.length > 0 && ( */}
          {hasPermission && (
            <Button
              className="cast-data__btn"
              type="default"
              onClick={() => setOpen(true)}
              disabled={selectedRowKeys.length === 0}
            >
              Update Status
            </Button>
          )}
          {/* )} */}
          <Table
            rowSelection={hasPermission ? rowSelection : null}
            rowKey={(record) => record._id}
            columns={columns}
            // dataSource={codes}
            // dataSource={getTableDataSource()}
            dataSource={codes}
            loading={loading}
            pagination={{
              defaultPageSize: "7",
              responsive: true,
              hideOnSinglePage: true,
              pageSizeOptions: ["7", "10", "50", "100"],
            }}
            scroll={{ x: 800 }}
            rowClassName={(record, index) => (index % 2 === 0 ? "even" : "odd")}
            onChange={(pagination, filters, sorter, extra) => {
              setFilteredData(extra.currentDataSource || codes); // Update filtered data
            }}
          />
        </div>
      </section>
      <Modal
        open={open}
        title="Update status"
        onCancel={handleCancel}
        footer={
          <>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleChangeStatus}
              disabled={!status}
            >
              Update
            </Button>
          </>
        }
      >
        <Select
          className="search__searchField py-1 wd-100"
          placeholder="Select status"
          dropdownStyle={dropdownStyle}
          onSelect={(value) => setStatus(value ? "Active" : "Inactive")}
          defaultValue={status}
          value={status}
        >
          <Select.Option value={true}>Active</Select.Option>
          <Select.Option value={false}>Inactive</Select.Option>
        </Select>
      </Modal>
    </>
  );
}

export default CodeDetails;
